<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToImport',
	componentName: 'ToImport'
});
</script>
<template>
	<span :key="langKey" :flex="flex ? flex : null" class="to-import" :class="setClass">
		<input v-show="false" ref="fileInput" type="file" :accept="accept" @change="handleChange" />
		<to-button v-if="props.type === 'button'" ref="button" class="to-import-button" icon="import" :disabled="props.disabled" expand @click.stop="handleFloat(button)">{{ props.buttonText || t('to.import.button') }}</to-button>
		<to-icon v-else ref="iconEl" link value="import" class="to-import-icon" :disabled="props.disabled" @click.stop="handleFloat(iconEl)"></to-icon>
		<to-float ref="layer" width="25" class="to-import-layer" lazy :before-open="props.beforeOpen" :before-close="props.beforeClose" @opened="handleFloatOpened" @closed="handleFloatClosed">
			<to-scroll>
				<div class="to-import-file" :class="setFileClass" @dragover.prevent="handleDragOver" @dragleave.prevent="handleDragLeave" @click="handleImport" @drop.prevent="handleDrop">
					<to-icon :value="getIcon" class="to-import-file-icon"></to-icon>
					<div class="to-import-file-name">
						<span class="to-import-file-name-text">{{ errorTip || fileName || t('to.import.dragText') }}</span>
					</div>
					<div v-if="state || hasDownloadTemplate()" class="to-import-file-fn">
						<to-button v-if="state" icon="reset" class="to-import-reset" @click.stop="handleReset">{{ t('to.import.reset') }}</to-button>
						<to-button v-else-if="hasDownloadTemplate()" class="to-import-download" icon="download" @click.stop="downloadTem">{{ t('to.import.downloadTemplate') }}</to-button>
					</div>
				</div>
			</to-scroll>
		</to-float>
	</span>
</template>

<script lang="ts" setup>
import { ref, computed, getCurrentInstance, onBeforeUnmount } from 'vue'
import { fileAjax } from '@touchvue/utils'
import { ToButton, ToIcon, ToFloat } from '@touchvue/components'
import { t, langKey } from '@touchvue/locale'

// 定义组件名称和选项


export interface ImportProps {
	flex?: boolean
	mode?: string
	type?: string
	accept?: string
	buttonText?: string
	name?: string
	action?: string
	beforeStart?: (file) => boolean | Promise<boolean>
	closed?: () => any
	beforeClose?: () => boolean | Promise<boolean>
	opened?: () => any
	beforeOpen?: () => boolean | Promise<boolean>
	headers?: object | null
	withCredentials?: boolean
	data?: object | null
	disabled?: boolean
}
const props = withDefaults(defineProps<ImportProps>(), {
	flex: false,
	mode: 'default',
	buttonText: '',
	accept: '',
	type: 'button',
	name: 'importfile',
	action: '',
	headers: () => null,
	withCredentials: false,
	data: () => null,
	beforeStart: () => true,
	disabled: false,
	beforeOpen: () => true,
	opened: () => true,
	beforeClose: () => true,
	closed: () => true
})

const instance = getCurrentInstance()
const hasDownloadTemplate = () => {
	return !!instance.vnode.props.onDownloadTemplate
}
const emit = defineEmits(['success', 'error', 'start', 'progress', 'download-template'])

const state = ref('')
const fileName = ref('')
const layer = ref(null)
const iconEl = ref(null)
const button = ref(null)
const fileInput = ref(null)
const btnIcon = ref('import')
const isDragOver = ref(false)
const isFloatOpen = ref(false)
const errorTip = ref('')

const setClass = computed(() => {
	let arr: string[] = []
	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}
	if (state.value) {
		arr.push(`state-${state.value}`)
	}
	return arr
})

const setFileClass = computed(() => {
	let arr: string[] = []
	if (isDragOver.value) {
		arr.push('is-dragover')
	}
	return arr
})

const fileTypeIcons = {
	excel: ['.xls', '.xlsx', '.csv'],
	img: ['.png', '.jpg', '.gif', '.apng', '.webp'],
	ppt: ['.ppt', '.pptx', '.ppsx'],
	rar: ['.rar', '.zip', '.tar', '.gz', '.tgz'],
	video: ['.rm', '.rmvb', '.avi', '.mkv', '.mp4', '.wmv', '.mov', '.movie', '.mpeg', '.mpg', '.qt'],
	voice: ['.mp2', '.mp3', '.wma', '.midi'],
	word: ['.doc', '.docx']
}

const getIcon = computed(() => {
	// 特殊状态处理
	if (!state.value) {
		return 'upload'
	}
	if (state.value === 'uploading') {
		return 'loading'
	}
	if (state.value === 'success') {
		return 'ok'
	}
	if (state.value === 'error') {
		return 'close'
	}

	// 校验入参，为空直接返回默认值
	if (!fileName.value || typeof fileName.value !== 'string') {
		return 'default'
	}

	// 提取文件后缀并统一转为小写
	let ext
	if (fileName.value.startsWith('.')) {
		// 直接传入后缀的情况（如 ".XLSX"）
		ext = fileName.value.toLowerCase()
	} else {
		// 传入完整文件名的情况（如 "Test.XLSX"）
		const lastDotIndex = fileName.value.lastIndexOf('.')
		// 无后缀的文件（如 "README"）
		if (lastDotIndex === -1) {
			return 'default'
		}
		ext = fileName.value.slice(lastDotIndex).toLowerCase()
	}

	// 遍历映射关系（映射表已为小写），匹配对应的图标类型
	for (const [iconType, extList] of Object.entries(fileTypeIcons)) {
		if (extList.includes(ext)) {
			return iconType
		}
	}

	// 未匹配到任何类型，返回默认值
	return 'default'
})

// 验证文件类型是否符合 accept 属性
const validateFileType = (file: File): boolean => {
	// 如果没有设置 accept，则允许所有类型
	if (!props.accept) return true

	const fileName = file.name.toLowerCase()
	const acceptRules = props.accept.split(',').map(rule => rule.trim().toLowerCase())

	// 检查文件是否匹配任意一条 accept 规则
	return acceptRules.some(rule => {
		if (rule.startsWith('.')) {
			// 扩展名规则（如 ".xlsx,.xls"）
			return fileName.endsWith(rule)
		} else if (rule === '*/*') {
			// 允许所有类型
			return true
		} else if (rule.includes('/*')) {
			// MIME 类型通配符（如 "image/*", "application/*"）
			const typePrefix = rule.split('/*')[0]
			return file.type.toLowerCase().startsWith(typePrefix + '/')
		} else {
			// 具体 MIME 类型（如 "application/json"）
			return file.type.toLowerCase() === rule
		}
	})
}

const handleChange = async (e: Event) => {
	const files = (e.target as HTMLInputElement).files
	if (files && files.length > 0) {
		const file = files[0]
		if (validateFileType(file)) {
			await processFile(file)
		} else {
			// 文件类型不通过校验，显示错误状态
			state.value = 'error'
			errorTip.value = t('to.import.error.fileType')
			fileName.value = ''
			// 清空 input 以便重新选择
			;(e.target as HTMLInputElement).value = ''
		}
	}
}

async function handleUpload(file) {
	let formData = new FormData()
	formData.append(props.name, file)
	if (props.data) {
		Object.keys(props.data).forEach(key => {
			formData.append(key, props.data[key])
		})
	}
	state.value = 'uploading'
	fileAjax({
		action: props.action,
		data: formData,
		headers: props.headers,
		withCredentials: props.withCredentials,
		onError: (err, status) => {
			state.value = 'error'
			emit('error', err, status, file)
			// 根据错误类型做不同处理
			switch (err.type) {
				case 'network_error':
					errorTip.value = t('to.import.error.network') + err.message
					break
				case 'http_error':
					errorTip.value = t('to.import.error.server') + `(${status})：${err.response?.message || err.message}`
					break
				case 'parse_error':
					errorTip.value = t('to.import.error.dataFormat') + err.message
					break
			}
		},
		onSuccess: (data, status) => {
			let _data = JSON.parse(JSON.stringify(data))
			btnIcon.value = 'import'
			state.value = 'success'
			emit('success', _data, status, file)
		},
		onProgress: e => {
			state.value = 'uploading'
			emit('progress', e.loaded, e.total, file)
		}
	})
}
const handleFloat = el => {
	if (props.disabled) return false
	layer.value.toggle(el)
}

const handleImport = () => {
	fileInput.value.click()
}

const downloadTem = () => {
	emit('download-template')
}

// 重置组件状态
const handleReset = () => {
	state.value = ''
	fileName.value = ''
	errorTip.value = ''
	btnIcon.value = 'import'
	if (fileInput.value) {
		fileInput.value.value = ''
	}
}

const handleFloatOpened = () => {
	isFloatOpen.value = true
	addPasteListener()
	if (props.opened) {
		props.opened()
	}
}

// 处理 float 关闭回调
const handleFloatClosed = () => {
	removePasteListener()
	isFloatOpen.value = false
	if (props.closed) {
		props.closed()
	}
}

// 拖拽上传相关方法
const handleDragOver = () => {
	isDragOver.value = true
}

const handleDragLeave = () => {
	isDragOver.value = false
}

const handleDrop = async (e: DragEvent) => {
	isDragOver.value = false
	const files = e.dataTransfer?.files
	if (files && files.length > 0) {
		const file = files[0]
		if (validateFileType(file)) {
			await processFile(file)
		} else {
			// 文件类型不正确时的状态处理
			state.value = 'error'
			errorTip.value = t('to.import.error.fileType')
			fileName.value = ''
		}
	}
}

// 粘贴上传相关方法
const handlePaste = async (e: ClipboardEvent) => {
	// 只在下拉框打开时处理粘贴
	if (!isFloatOpen.value) return

	const items = e.clipboardData?.items
	if (!items) return

	for (let i = 0; i < items.length; i++) {
		const item = items[i]
		if (item.kind === 'file') {
			const file = item.getAsFile()
			if (file && validateFileType(file)) {
				e.preventDefault()
				await processFile(file)
				break
			} else if (file) {
				// 文件类型不正确时的状态处理
				e.preventDefault()
				state.value = 'error'
				errorTip.value = t('to.import.error.fileType')
				fileName.value = ''
				break
			}
		}
	}
}

// 添加全局粘贴监听
const addPasteListener = () => {
	document.body.addEventListener('paste', handlePaste)
}

// 移除全局粘贴监听
const removePasteListener = () => {
	document.body.removeEventListener('paste', handlePaste)
}

// 组件卸载前移除监听
onBeforeUnmount(() => {
	removePasteListener()
})

// 处理文件的统一方法
const processFile = async (file: File) => {
	let flag = true
	if (props.beforeStart) {
		if (typeof props.beforeStart === 'function') {
			const result = props.beforeStart(file)
			if (result && typeof (result as Promise<boolean>).then === 'function') {
				flag = await result
			} else {
				flag = !!result
			}
		} else {
			flag = !!props.beforeStart
		}
	}
	if (!flag || !props.action) return false
	fileName.value = file.name
	btnIcon.value = 'loading'
	emit('start', file)
	handleUpload(file)
}
</script>
